import * as React from "react";
import { Button, type TypeButtonProps } from "@sproutsocial/seeds-react-button";
import { cn } from "./Common/cn";

/**
 * v3 MenuButton (split button) surface, rendered with CSS classes from menu.css
 * (imported via @sproutsocial/seeds-react-menu/base/css) instead of
 * styled-components. Each export keeps its previous name.
 *
 * MainButton / TriggerButton square off the inner corners so the two halves
 * read as one control. The class rules use doubled selectors to reproduce the
 * old `&&` specificity bump; Button forwards `className` and spreads `data-*`
 * (including Base UI's `data-popup-open`) onto its root DOM node, so the chevron
 * rotation selector `.seeds-menu-button-trigger[data-popup-open] .Icon-svg`
 * still resolves.
 *
 * These forward refs and spread all props so Base UI's `Menu.Trigger render`
 * can inject its ref, `data-popup-open`, and event handlers onto the trigger —
 * matching what `styled(Button)` did previously.
 */

export const MenuButtonContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn("seeds-menu-button-container", className)}
    {...rest}
  />
));
MenuButtonContainer.displayName = "MenuButtonContainer";

// Square off the inner (right) corners of the main button.
export const MainButton = React.forwardRef<HTMLButtonElement, TypeButtonProps>(
  ({ className, ...rest }, ref) => (
    <Button
      ref={ref}
      className={cn("seeds-menu-button-main", className)}
      {...rest}
    />
  )
);
MainButton.displayName = "MainButton";

// Square off the inner (left) corners of the chevron trigger button. The
// chevron rotates 180° while the menu is open: Base UI's Menu.Trigger sets
// `data-popup-open` on this element (Button spreads it onto its root DOM node).
export const TriggerButton = React.forwardRef<
  HTMLButtonElement,
  TypeButtonProps
>(({ className, ...rest }, ref) => (
  <Button
    ref={ref}
    className={cn("seeds-menu-button-trigger", className)}
    {...rest}
  />
));
TriggerButton.displayName = "TriggerButton";
